import { get, post, put } from "@/api/client";

export type ContentType =
  | "document"
  | "rule"
  | "workflow"
  | "checklist"
  | "contact_card"
  | "video"
  | "link";

// -- Per-type content shapes (mirror backend.domain.content_blocks) ----------
export interface Attachment {
  file_id: string;
  filename: string;
  mime: string;
  size: number;
  caption?: string;
}
export type WorkflowNodeType = "start" | "step" | "decision" | "end";
export interface WorkflowNode {
  id: string;
  type: WorkflowNodeType;
  label: string;
  position: { x: number; y: number };
}
export interface WorkflowEdge {
  id: string;
  source: string;
  target: string;
  label?: string;
}
export interface ChecklistItem {
  text: string;
  required: boolean;
  checked?: boolean; // employee's tick state (employee program view only)
}
export interface Contact {
  name: string;
  role: string;
  email: string;
  phone: string;
  note: string;
}
export interface LinkItem {
  label: string;
  url: string;
  description: string;
}

/** Loose union of every type's fields (all optional); cast per content_type. */
export interface ContentData {
  // document / rule
  markdown?: string;
  attachments?: Attachment[];
  // workflow
  nodes?: WorkflowNode[];
  edges?: WorkflowEdge[];
  // checklist
  intro?: string;
  items?: ChecklistItem[];
  // contact_card
  contacts?: Contact[];
  // video
  url?: string;
  provider?: "youtube" | "vimeo" | "file";
  caption?: string;
  // link
  links?: LinkItem[];
}

/** A module's content per language; keys are "nl" / "en". Some may be absent. */
export type LocalizedContent = Record<string, ContentData>;
export type TitleMap = Record<string, string>;

/** Pick a language from a localized content map, falling back to any other. */
export function pickContent(localized: LocalizedContent | undefined, lang: string): ContentData {
  const m = localized || {};
  return m[lang] || m.nl || m.en || Object.values(m)[0] || {};
}

/** Pick a language from a title map, falling back to the primary/given title. */
export function pickTitle(map: TitleMap | undefined, lang: string, fallback = ""): string {
  const m = map || {};
  return m[lang] || m.nl || m.en || fallback;
}

/** Default empty content for a freshly selected content type. */
export function emptyContent(type: ContentType): ContentData {
  switch (type) {
    case "document":
    case "rule":
      return { markdown: "", attachments: [] };
    case "workflow":
      return { nodes: [], edges: [] };
    case "checklist":
      return { intro: "", items: [] };
    case "contact_card":
      return { contacts: [] };
    case "video":
      return { url: "", caption: "" };
    case "link":
      return { links: [] };
  }
}

export interface ModuleSummary {
  id: string;
  tenant_id: string;
  title: string;
  title_i18n?: TitleMap;
  slug: string;
  content_type: ContentType;
  status: "draft" | "published" | "archived";
  requires_acknowledgement: boolean;
  current_version: number | null;
  created_at: string;
  updated_at: string;
}

export interface ModuleVersionInfo {
  id: string;
  module_id: string;
  version_no: number;
  is_published: boolean;
  published_at: string | null;
  created_at: string;
}

export interface ModuleDetail extends ModuleSummary {
  versions: ModuleVersionInfo[];
  editable_content: LocalizedContent; // {lang: content}
  editable_body: string; // legacy alias = primary-language markdown
  has_unpublished_changes: boolean;
}

export interface CreateModuleInput {
  title: string;
  title_i18n?: TitleMap;
  content_type: ContentType;
  requires_acknowledgement: boolean;
  content: LocalizedContent;
}

export interface UpdateModuleInput {
  title?: string;
  title_i18n?: TitleMap;
  content_type?: ContentType;
  requires_acknowledgement?: boolean;
  content?: LocalizedContent;
}

async function unwrap<T>(p: Promise<{ ok: true; data: T } | { ok: false; err: { message: string } }>): Promise<T> {
  const r = await p;
  if (!r.ok) throw new Error(r.err.message);
  return r.data;
}

export const listModules = (signal?: AbortSignal) => unwrap<ModuleSummary[]>(get("/api/modules", signal));
export const getModule = (id: string, signal?: AbortSignal) => unwrap<ModuleDetail>(get(`/api/modules/${id}`, signal));
export const createModule = (input: CreateModuleInput) => unwrap<ModuleDetail>(post("/api/modules", input));
export const updateModule = (id: string, input: UpdateModuleInput) => unwrap<ModuleDetail>(put(`/api/modules/${id}`, input));
export const publishModule = (id: string) => unwrap<ModuleDetail>(post(`/api/modules/${id}/publish`));
export const archiveModule = (id: string) => unwrap<ModuleDetail>(post(`/api/modules/${id}/archive`));

export interface VariableDef {
  key: string;
  label: string;
  example: string;
}

export async function fetchVariables(signal?: AbortSignal): Promise<VariableDef[]> {
  const d = await unwrap<{ variables: VariableDef[] }>(get("/api/content/variables", signal));
  return d.variables;
}

export async function fetchPreviewContext(signal?: AbortSignal): Promise<Record<string, string>> {
  const d = await unwrap<{ context: Record<string, string> }>(get("/api/content/preview-context", signal));
  return d.context;
}
